import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Star, Award } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface BabyMilestonesProps {
  babyId: string;
}

const MILESTONE_TYPES = [
  { value: "first_smile", label: "First Smile", icon: "😊" },
  { value: "first_word", label: "First Word", icon: "💬" },
  { value: "first_step", label: "First Step", icon: "👣" },
  { value: "first_tooth", label: "First Tooth", icon: "🦷" },
  { value: "crawl", label: "Started Crawling", icon: "🐛" },
  { value: "walk", label: "Started Walking", icon: "🚶" },
  { value: "solid_food", label: "First Solid Food", icon: "🍎" },
  { value: "potty_trained", label: "Potty Trained", icon: "🚽" },
  { value: "sleep_through_night", label: "Slept Through Night", icon: "💤" },
  { value: "other", label: "Other Milestone", icon: "⭐" },
];

export function BabyMilestones({ babyId }: BabyMilestonesProps) {
  const [milestones, setMilestones] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [milestoneType, setMilestoneType] = useState("");
  const [milestoneDate, setMilestoneDate] = useState("");
  const [notes, setNotes] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    loadMilestones();
  }, [babyId]);

  const loadMilestones = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("baby_milestones")
      .select("*")
      .eq("user_id", user.id)
      .eq("baby_id", babyId)
      .order("milestone_date", { ascending: false });

    if (data) setMilestones(data);
  };

  const handleAddMilestone = async () => {
    if (!milestoneType || !milestoneDate) {
      toast({
        title: "Missing information",
        description: "Please select milestone type and date",
        variant: "destructive",
      });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("baby_milestones")
      .insert({
        user_id: user.id,
        baby_id: babyId,
        milestone_type: milestoneType,
        milestone_date: milestoneDate,
        notes: notes,
        title: milestoneType,
      });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to add milestone",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Milestone added! 🎉",
      description: "This precious moment has been captured",
    });

    setIsDialogOpen(false);
    setMilestoneType("");
    setMilestoneDate("");
    setNotes("");
    loadMilestones();
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 bg-secondary/30">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Star className="h-5 w-5 text-primary" />
          Why Track Milestones?
        </h3>
        <p className="text-sm text-muted-foreground">
          Every child develops at their own pace. By tracking milestones, you'll understand your baby's unique 
          developmental journey, celebrate achievements, identify areas that might need support, and create a 
          beautiful timeline of growth to treasure forever. These moments pass so quickly-capturing them helps 
          you remember and appreciate each stage.
        </p>
      </Card>

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Milestones Achieved</h3>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Milestone
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record a Milestone</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="milestoneType">Milestone Type</Label>
                <Select value={milestoneType} onValueChange={setMilestoneType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select milestone" />
                  </SelectTrigger>
                  <SelectContent>
                    {MILESTONE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="milestoneDate">Date Achieved</Label>
                <Input
                  id="milestoneDate"
                  type="date"
                  value={milestoneDate}
                  onChange={(e) => setMilestoneDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Describe this special moment..."
                  rows={3}
                />
              </div>
              <Button onClick={handleAddMilestone} className="w-full">
                Save Milestone
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {milestones.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <Award className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No milestones recorded yet. Add your baby's first achievement!</p>
          </Card>
        ) : (
          milestones.map((milestone) => {
            const type = MILESTONE_TYPES.find((t) => t.value === milestone.milestone_type);
            return (
              <Card key={milestone.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{type?.icon || "⭐"}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold">{type?.label || "Milestone"}</h4>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(milestone.milestone_date), "MMM d, yyyy")}
                      </span>
                    </div>
                    {milestone.notes && (
                      <p className="text-sm text-muted-foreground">{milestone.notes}</p>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
